// apps/embed/src/chat-widget/ChatWidget.tsx
//
// The floating website chat widget — a launcher Fab + a panel. What the panel
// holds depends on the business's bootstrap `mode`:
//   * 'chat'       → the conversation (AI bot and/or human staff). By default the
//                    visitor first fills in the pre-chat form (name + email +
//                    phone, then the first message); a business that does not ask
//                    for contact details first (`askContactDetails: false`) lets
//                    them write the first message straight into the empty
//                    conversation pane.
//   * 'email_only' → a "leave a message" form that emails the business contact
//                    (no live chat: both AI and human support are off). It always
//                    asks for contact details.
//   * 'hidden'     → nothing (no live chat and no contact email) — render null.
// Reuses the SHARED conversation pane (ChatMessageList / ChatComposer /
// ChatPresenceDot) + the shared pre-chat form, so it looks + behaves like the rest
// of the product. Pure browser — talks to /v1 only, never a server package.

import { useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, type Theme } from '@mui/material/styles';
import type { WidgetBrandColors } from '@tourtask/types/api/v1/WidgetBrandColors';
import type { TFunc } from '@tourtask/types/i18n';
import type { ResultError } from '@tourtask/types/result';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { AlertBox } from '@tourtask/ui/components/AlertBox';
import { SmoothCollapse } from '@tourtask/ui/components/SmoothCollapse';
import { useLastDefined } from '@tourtask/ui/hooks/useLastDefined';
import { ChatWidgetShell } from '@tourtask/ui/components/chat/ChatWidgetShell';
import { ChatMessageList } from '@tourtask/ui/components/chat/ChatMessageList';
import { ChatComposer } from '@tourtask/ui/components/chat/ChatComposer';
import { useChatDraft } from '@tourtask/ui/components/chat/useChatDraft';
import { ChatPresenceChip } from '@tourtask/ui/components/chat/ChatPresenceChip';
import { useDraggableFab } from '@tourtask/ui/components/chat/useDraggableFab';
import LocalizationContext from '@tourtask/storefront/contexts/LocalizationContext';
import { fetchChatReference, type ChatReference, type WidgetChatMode } from '@tourtask/storefront/api/chat';
import { createWidgetTheme } from '@tourtask/storefront/themes/createWidgetTheme';
import { isWidgetSetupRefusal } from '@tourtask/storefront/api/widgetSetupRefusal';
import { WidgetErrorAlert } from '@tourtask/storefront/components/general/WidgetErrorAlert';
import { WidgetLanguageAlert } from '@tourtask/storefront/components/general/WidgetLanguageAlert';
import type { WidgetLanguageProblem } from '@tourtask/storefront/utils/widgetLanguage';
import { useWebsiteChat } from './useWebsiteChat';
import { PreChatForm } from '@tourtask/ui/components/chat/PreChatForm';
import { ChatEmailForm } from './ChatEmailForm';
import { ChatConnectionError } from './ChatConnectionError';

/**
 * Why the panel cannot hold a chat, decided at boot by `ChatWidgetBootstrap`, all three with no strings loaded:
 * the strings fetch failed for want of a connection (`connection`), the API refused the widget's setup — its
 * business ID or token (`refused`, `isWidgetSetupRefusal`) — or the host's `data-lang` names a language TourTask
 * does not support (`language`), in which nothing is loaded. Either way the launcher still opens, and the panel
 * says which it is.
 */
export type ChatBootProblem =
  | { kind: 'connection' }
  | { kind: 'refused'; error: ResultError }
  | { kind: 'language'; problem: WidgetLanguageProblem };

interface ChatWidgetProps {
  bizSlug: string;
  bizToken: string;
  /** The conversation's language, sent with every visitor message; `null` when the host's
   *  `data-lang` is absent, so the server answers in the business's default language
   *  (`readChatWidgetLanguages`). The chrome's own language is `langCode`. */
  visitorLocale: string | null;
  /** Why the panel cannot hold a chat, or `null` when it can. The panel then shows that notice
   *  rather than a placeholder-filled UI. */
  bootProblem: ChatBootProblem | null;
  /** Which bottom corner the launcher sits in before the visitor drags it.
   *  Comes from the host's `data-chat-position` attribute, which the WordPress
   *  plugin sets from its "Chat position" setting; defaults to the conventional
   *  right corner. The visitor can still drag it elsewhere — this only sets the
   *  starting corner. */
  defaultSide?: 'left' | 'right';
}

// Fallback chrome text while the panel shows a boot problem. No strings loaded (the
// fetch failed, or there is no language to fetch them in), so `t('Chat_with_us')`
// would render the `NO STRING IMPORTED…` placeholder — use a hardcoded label for the
// header + launcher aria instead.
const FALLBACK_TITLE = 'Chat with us';

// Above virtually any partner-site element; `chatThemeOf` lifts the portaled popups (the phone-country dropdown)
// above the panel.
export const CHAT_PANEL_Z = 2147483000;

/**
 * The chat widget's theme: the business's brand as MUI's primary once the reference carries it (docs/brand-colours.md
 * phase 4; MUI's own before, and from an older API), and MUI's Autocomplete popper above the panel. That dropdown
 * portals to <body> at the default popper z-index (theme.zIndex.modal = 1300), which put the open list BEHIND the
 * panel on host pages ("the list appears below the chat widget"); scoped to this theme, the override reaches only the
 * chat widget's own poppers, nothing on the host page or the booking bundle, and no prop through the shared
 * CountrySelectorInput / FormAutocomplete chain.
 */
export function chatThemeOf(brand: WidgetBrandColors | undefined): Theme {
  return createWidgetTheme({
    brand,
    components: { MuiAutocomplete: { styleOverrides: { popper: { zIndex: CHAT_PANEL_Z + 1 } } } },
  });
}

/** The conversation pane's message box. A message that did not go through comes back into it,
 *  and leaves it once it got through after all (`useChatDraft`). Its text lives as long as the
 *  pane: when the pre-chat form takes over, the form holds the message instead. */
function PaneComposer({
  send,
  recovered,
  isSending,
  t,
}: {
  send: (text: string) => Promise<boolean>;
  recovered: { text: string } | null;
  isSending: boolean;
  t: TFunc;
}) {
  const draft = useChatDraft(send, recovered);
  return <ChatComposer {...draft} isSending={isSending} t={t} autoFocus />;
}

export function ChatWidget({
  bizSlug,
  bizToken,
  visitorLocale,
  bootProblem,
  defaultSide = 'right',
}: ChatWidgetProps) {
  const { getStr, langCode } = useContext(LocalizationContext);
  const t = getStr;
  const [open, setOpen] = useState(false);

  // Widget bootstrap (mode + presence flag + the country list for the phone
  // field), fetched once at mount so we know whether/how to render BEFORE showing
  // the launcher — a 'hidden' business never even flashes a launcher. Not fetched
  // when the host's language is unsupported: the panel then only names the code.
  const [reference, setReference] = useState<ChatReference | null>(null);
  const [referenceError, setReferenceError] = useState(false);
  // The API's refusal of the widget's setup, answered to the bootstrap fetch: with
  // the strings loaded, typically a page the business has not registered.
  const [referenceRefusal, setReferenceRefusal] = useState<ResultError | null>(null);
  const languageUnsupported = bootProblem?.kind === 'language';
  useEffect(() => {
    // Fetched in the chrome language, never before it is known. The entry starts the localization provider in
    // that language, so this runs once; it used to fetch in English while the language was still empty, throw
    // that answer away when the language arrived, and fetch again (docs/embed-size-and-speed.md §6.1 #6).
    if (languageUnsupported || langCode === '') return;
    let cancelled = false;
    setReferenceError(false);
    setReferenceRefusal(null);
    void fetchChatReference({ bizSlug, bizToken, lang: langCode }).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setReference(res.data);
      } else {
        setReferenceError(true);
        if (isWidgetSetupRefusal(res.error)) setReferenceRefusal(res.error);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [bizSlug, bizToken, langCode, languageUnsupported]);
  // Whatever refused the widget's setup, the strings fetch or the bootstrap fetch:
  // the panel shows the reason, and nothing else talks to the API.
  const refusal: ResultError | null = bootProblem?.kind === 'refused' ? bootProblem.error : referenceRefusal;
  // The business's brand, from the reference (absent before it loads, and from an older API).
  const brand = reference?.brand;
  const brandTheme = useMemo(() => chatThemeOf(brand), [brand]);

  // While loading, mode is null → render nothing yet. On a bootstrap error fall
  // back to live chat (the common case) rather than hide an enabled widget over a
  // transient blip — the start flow has its own error UI.
  const mode: WidgetChatMode | null = reference?.mode ?? (referenceError ? 'chat' : null);
  const isChat = mode === 'chat';
  // The live-chat hook drives nothing in email-only / hidden mode, nor while the API
  // refuses the widget's setup. `aiEnabled` gates the bot typing indicator; on the
  // bootstrap-error fallback (reference null) it stays false — no dots without
  // knowing a bot exists.
  const chat = useWebsiteChat({
    bizSlug,
    bizToken,
    open,
    locale: visitorLocale,
    enabled: isChat && refusal === null,
    aiEnabled: reference?.aiEnabled ?? false,
  });
  // Whether the visitor gives contact details before the first message. An API
  // from before the setting omits it, and the bundle deploys before the API:
  // absent means ask, as every widget did then. A start the server refused for
  // lack of them means the business has just turned the form back on.
  const askContactDetails = reference?.askContactDetails !== false || chat.contactDetailsRequired;
  // What the conversation pane's error line reports: the start (before the first
  // message is in) or a send. Kept through the line's closing animation.
  const failed = useLastDefined<'start' | 'send'>(
    chat.error === null ? null : chat.phase === 'form' ? 'start' : 'send',
  );

  // The launcher is draggable on every surface (same hook as the backoffice
  // widget). Starts in the host-chosen bottom corner (`defaultSide`, default
  // right); once dragged it remembers its own position.
  const { containerPos, side, vertical, dragging, fabProps, guardClick } = useDraggableFab({
    storageKey: 'tt-website-chat-pos',
    defaultSide,
  });

  // A legitimately 'hidden' business never renders (the server resolved the mode,
  // so its strings loaded too). Otherwise wait until the mode is known — UNLESS we
  // already know the panel cannot hold a chat (`bootProblem`: the API unreachable,
  // the widget's setup refused, or an unsupported `data-lang`), in which case we
  // render now to say so instead of a placeholder-filled UI. A refused bootstrap
  // fetch falls back to the chat mode below, so it renders too.
  if (mode === 'hidden') return null;
  if (mode === null && bootProblem === null) return null;

  // Header + launcher label: fall back to a hardcoded title when no strings
  // loaded, since `t('Chat_with_us')` would render the placeholder.
  const title = bootProblem !== null ? FALLBACK_TITLE : t('Chat_with_us');

  // Presence chip (chat mode only): a green "Online" only when a human is genuinely
  // available; NO "offline" chip when the AI bot covers the conversation (it always
  // answers) — "offline" shows only for a human-only business with nobody available.
  // `aiEnabled` is null until `reference` loads (so we actually KNOW it): on a
  // bootstrap-error fallback to chat, reference is null and the shared chip must not
  // assert "offline" without data. Same policy as the backoffice support widget.
  const presence = (
    <ChatPresenceChip
      humanOnline={chat.supportOnline}
      aiEnabled={reference ? reference.aiEnabled : null}
      t={t}
      onlineLabel={t('We_are_online')}
      offlineLabel={t('We_are_currently_offline')}
    />
  );

  return (
    <ThemeProvider theme={brandTheme}>
      <ChatWidgetShell
        // Draggable launcher (same hook as the backoffice widget). `dir="ltr"` keeps
        // the chrome stable even on RTL partner sites.
        geometry={{ containerPos, side, vertical }}
        zIndex={CHAT_PANEL_Z}
        // The header and the launcher are the brand's band (white on the accent darkened until it reads), the colours
        // of the shop's control bar: on an orange the accent's own launcher carried a black icon (the owner, 2026-11-03).
        bandColors={brand === undefined ? undefined : { bg: `#${brand.barColor}`, color: `#${brand.onBarColor}` }}
        dir="ltr"
        open={open}
        onLauncherClick={() => guardClick(() => setOpen((v) => !v))}
        onClose={() => setOpen(false)}
        title={title}
        // No presence chip while the panel shows a boot problem or a refusal, or in email-only/hidden mode.
        presence={isChat && bootProblem === null && refusal === null ? presence : null}
        closeLabel={bootProblem !== null ? 'Close' : t('Close')}
        launcherLabel={title}
        unread={chat.unread}
        fabProps={fabProps}
        // Drag to reposition; touchAction:none stops a touch-drag from scrolling the page.
        fabSx={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab' }}
      >
        {bootProblem?.kind === 'connection' ? (
          // API unreachable (strings fetch failed) — show a connection notice
          // rather than a UI full of `NO STRING IMPORTED…` placeholders.
          <ChatConnectionError />
        ) : bootProblem?.kind === 'language' ? (
          // The host's `data-lang` names a language TourTask does not support: name
          // it and the supported codes, so whoever set the widget up can fix it.
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <WidgetLanguageAlert problem={bootProblem.problem} widget="chat" />
          </Box>
        ) : refusal !== null ? (
          // The API refused the widget's setup (a wrong business ID or token, a page
          // the business has not registered): say why, never "try again later".
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <WidgetErrorAlert error={refusal} />
          </Box>
        ) : mode === 'frozen' ? (
          // Account frozen for unpaid TourTask invoices — the whole widget is
          // disabled (no AI bot, no human handoff, no leave-a-message form). The
          // launcher still opens so the visitor (and the operator browsing their
          // own site) sees WHY it's off, nudging the operator to pay.
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <AlertBox type="warning" mb={0}>
              {t('WEBSITE_CHAT_DISABLED_UNPAID_INVOICES_INFO')}
            </AlertBox>
          </Box>
        ) : !isChat ? (
          reference ? (
            <ChatEmailForm
              bizSlug={bizSlug}
              bizToken={bizToken}
              t={t}
              langCode={langCode || 'en'}
              countries={reference.countries}
            />
          ) : null
        ) : chat.phase === 'form' && askContactDetails ? (
          reference ? (
            <PreChatForm
              t={t}
              countriesWithNames={reference.countries}
              starting={chat.starting}
              error={chat.error}
              initialMessage={chat.refusedFirstMessage ?? undefined}
              onStart={(v) => void chat.start(v)}
            />
          ) : referenceError ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                {t('Chat_could_not_be_started')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )
        ) : (
          // The conversation — also before the first message when the business
          // lets visitors write straight away: the same pane, whose first send
          // starts the thread (so the composer stays put as the chat begins).
          <>
            <ChatMessageList
              messages={chat.messages}
              ownSenderType="public_visitor"
              t={t}
              locale={langCode}
              emptyLabel={chat.phase === 'form' ? t('How_can_we_help') : undefined}
              // Dots while the start/send round-trip is in flight AND while the
              // bot composes its reply out-of-band (it arrives via the poll).
              typing={chat.sending || chat.botTyping}
            />
            <SmoothCollapse in={chat.error !== null} unmountOnExit>
              <Typography key={chat.failureCount} role="alert" variant="body2" color="error" sx={{ px: 1.5, pb: 1, textAlign: 'center' }}>
                {failed === 'send' ? t('Your_message_could_not_be_sent') : t('Chat_could_not_be_started')}
              </Typography>
            </SmoothCollapse>
            {chat.closed ? (
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {t('This_conversation_is_closed')}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => chat.startNew()}>
                  {t('Start_a_new_conversation')}
                </Button>
              </Box>
            ) : (
              <PaneComposer
                send={(text) => (chat.phase === 'form' ? chat.start({ firstMessage: text }) : chat.send(text))}
                recovered={chat.recoveredMessage}
                isSending={chat.sending || chat.starting}
                t={t}
              />
            )}
          </>
        )}
      </ChatWidgetShell>
    </ThemeProvider>
  );
}
